import './SparkChart.scss';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { Metric, Metrics } from '@blisspointmedia/bpm-types/dist/CrossChannel';
import { Formatter } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  getMetricFormatter,
  MetricFormatter
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { ColumnMetaData } from '@src/Components/MetricsTable/metrics-table-utils';
import { METRIC_OPTIONS } from '../../Pages/CrossChannel/cross-channel-constants';
import {
  joinPrimaryAndComparisonData,
  makePrettyDateRange
} from '../../Pages/CrossChannel/cross-channel-utils';
import { sharedColumnMetaDataMap } from '../../Pages/SingleChannel/Channels/shared-performance-utils';
import { Brand0, Brand30, Brand50, Brand60 } from '../../utils/colors';
import { formatDateLabel } from '../../utils/date-utils';
import { withRatioAggregators } from '../../utils/metrics/ratioAggregation';
import { downloadPNG, exportMultiSheetToExcel, Sheet } from '../../utils/download-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { LINE_STROKE_WIDTH } from '../Charts/ChartConstants';
import { ChartTooltipV2 } from '../Charts/ChartTooltip/ChartTooltipV2';
import { Placement, Size, StretchMode } from '../designConstants';
import { DownloadDropdown } from '../DownloadDropdown/DownloadDropdown';
import { Dropdown } from '../Dropdown/Dropdown';
import { DropdownToggleType } from '../Dropdown/dropdown-constants';
import { GraphTableToggleButton } from '../GraphTableToggleButton/GraphTableToggleButton';
import { InteractiveDiv } from '../InteractiveEl';
import { readServerValue } from '../MetricsTable/server-metric-values';
import { PercentChange } from '../PercentChange/PercentChange';
import { BPMTable, TypedHeader } from '../StickyTable/BPMTable';
import { CellRenderer, SuperTopItemType } from '../StickyTable/StickyTable';

// Rough upper bound on the tooltip's width. When the cursor sits within this many
// pixels of the right viewport edge, the tooltip flips to the cursor's left side
// so it doesn't spill off the page.
const TOOLTIP_REVERSE_THRESHOLD_PX = 300;

const DEFAULT_COLUMN_META_DATA_MAP = withRatioAggregators(sharedColumnMetaDataMap);

const MetricTotalType = {
  TOTAL: 'total',
  AVERAGE: 'average',
  OVERALL: 'overall'
} as const;
type MetricTotalTypeValue = (typeof MetricTotalType)[keyof typeof MetricTotalType];

const METRIC_TOTAL_TYPE_PRETTY_NAMES: Record<MetricTotalTypeValue, string> = {
  [MetricTotalType.TOTAL]: 'TOTAL',
  [MetricTotalType.AVERAGE]: 'DAILY AVG',
  [MetricTotalType.OVERALL]: 'OVERALL'
};

interface JoinedData<M> {
  date: string;
  date_other: string;
  metrics: M;
  metrics_other: M;
}

interface TotalsByDate<M> {
  date: string;
  metrics: M;
}

interface SparkTooltipPoint {
  date?: string;
  date_other?: string;
  metrics?: Record<string, number>;
  metrics_other?: Record<string, number>;
}

interface SparkTooltipContentProps {
  selectedMetric: string;
  prettyName: string;
  formatValue: Formatter;
  payload?: { payload?: SparkTooltipPoint }[];
}

const SparkTooltipContent = ({
  selectedMetric,
  prettyName,
  formatValue,
  payload
}: SparkTooltipContentProps) => {
  const point = payload?.[0]?.payload ?? {};
  const primaryHeaderLabel = formatDateLabel(point.date || '', {}, 'Day', true);
  const comparisonHeaderLabel = formatDateLabel(point.date_other || '', {}, 'Day', true);
  const primaryMetricValue = point.metrics?.[selectedMetric] ?? 0;
  const comparisonMetricValue = point.metrics_other?.[selectedMetric] ?? 0;
  return (
    <ChartTooltipV2
      headers={[primaryHeaderLabel, comparisonHeaderLabel]}
      items={[{ label: prettyName, value: formatValue(primaryMetricValue), color: Brand60 }]}
      comparisonItems={[
        { label: prettyName, value: formatValue(comparisonMetricValue), color: 'black' }
      ]}
    />
  );
};

interface SparkChartProps<M, K extends string> {
  data: TotalsByDate<M>[];
  otherData: TotalsByDate<M>[];
  defaultMetric?: K;
  metricOptions?: { label: string; value: K; disabled?: boolean }[];
  pngFileIdentifiers?: string[];
  pngLabel?: string;
  dataInfo?: Record<string, string>[];
  defaultValueToggle?: 'Avg';
  columnMetaDataMap?: Partial<Record<string, ColumnMetaData>>;
  invertedMetrics?: K[];
  formatter?: MetricFormatter;
  // SQL totals by metric option; replace the client-side total when the selected metric has one.
  serverTotals?: Partial<Record<string, number | null>>;
  otherServerTotals?: Partial<Record<string, number | null>>;
}

export const SparkChart = <
  K extends string = Metric,
  M extends Record<string, number> = Metrics & Record<string, number>
>({
  data,
  otherData,
  defaultMetric,
  metricOptions = METRIC_OPTIONS as { label: string; value: K }[],
  pngFileIdentifiers = [],
  pngLabel,
  dataInfo,
  defaultValueToggle,
  columnMetaDataMap = DEFAULT_COLUMN_META_DATA_MAP,
  invertedMetrics = [Metric.CPX, Metric.CPC, Metric.CPM] as K[],
  formatter,
  serverTotals,
  otherServerTotals
}: SparkChartProps<M, K>) => {
  const [selectedMetric, setSelectedMetric] = useState<K>(defaultMetric || metricOptions[0]?.value);
  const [graphView, setGraphView] = useState(true);
  const [tooltipReverseX, setTooltipReverseX] = useState(false);
  const [hover, setHover] = useState(false);
  const { currency_code: currencyCode } = useCompanyInfo();

  const metricFormatter = useMemo(
    () => formatter ?? getMetricFormatter(currencyCode),
    [formatter, currencyCode]
  );

  const isOverallMetric = useMemo(
    () => columnMetaDataMap[selectedMetric]?.aggregator !== undefined,
    [selectedMetric, columnMetaDataMap]
  );

  const getInitialValueToggle = (): MetricTotalTypeValue => {
    if (defaultValueToggle === 'Avg') return MetricTotalType.AVERAGE;
    if (isOverallMetric) return MetricTotalType.OVERALL;
    return MetricTotalType.TOTAL;
  };
  const [valueToggle, setValueToggle] = useState<MetricTotalTypeValue>(getInitialValueToggle);

  const { total, otherTotal, percentChange, average, otherAverage } = useMemo(() => {
    const sumMetric = (rows: TotalsByDate<M>[]) =>
      rows.reduce((acc, curr) => acc + (curr.metrics?.[selectedMetric] ?? 0), 0);
    // For metrics derived from two other metrics (CPC, CPM, CPX, ROAS), apply the metric's
    // aggregator over the summed inputs rather than averaging per-row values.
    const aggregateMetric = (rows: TotalsByDate<M>[]) => {
      const totals: Record<string, number> = {};
      rows.forEach(item => {
        Object.entries(item.metrics).forEach(([key, value]) => {
          if (typeof value === 'number') {
            totals[key] = (totals[key] || 0) + value;
          }
        });
      });
      const metricInfo = columnMetaDataMap[selectedMetric];
      return metricInfo && typeof metricInfo.aggregator === 'function'
        ? metricInfo.aggregator(totals)
        : 0;
    };

    const getTotal = (
      rows: TotalsByDate<M>[],
      server: Partial<Record<string, number | null>> | undefined
    ) => {
      const serverTotal = readServerValue(server, selectedMetric);
      if (serverTotal.found) return Number(serverTotal.value);
      return Number(isOverallMetric ? aggregateMetric(rows) : sumMetric(rows));
    };
    const totalNum = getTotal(data, serverTotals);
    const otherTotalNum = getTotal(otherData, otherServerTotals);
    const pct = Math.round(((totalNum - otherTotalNum) / otherTotalNum) * 100);
    return {
      total: totalNum,
      otherTotal: otherTotalNum,
      percentChange: Number.isFinite(pct) ? pct : '--',
      average: data.length > 0 ? totalNum / data.length : 0,
      otherAverage: otherData.length > 0 ? otherTotalNum / otherData.length : 0
    };
  }, [
    data,
    otherData,
    selectedMetric,
    isOverallMetric,
    columnMetaDataMap,
    serverTotals,
    otherServerTotals
  ]);

  const metricInverted = invertedMetrics.includes(selectedMetric);

  const metricsToPrettyNameMap = useMemo(() => {
    const prettyNameMap: Record<string, string> = {};
    for (const metric of metricOptions) {
      prettyNameMap[metric.value] = metric.label;
    }
    return prettyNameMap;
  }, [metricOptions]);

  const joinedData = useMemo(
    () => joinPrimaryAndComparisonData({ primaryData: data, comparisonData: otherData }),
    [data, otherData]
  );

  const [headers, superHeaders] = useMemo(() => {
    const headerList: TypedHeader<JoinedData<M>>[] = [
      {
        label: 'Date',
        name: 'date',
        width: 60,
        nonInteractive: true,
        renderer: item => formatDateLabel(item.date, {}, '', true) || '-'
      },
      {
        label: metricsToPrettyNameMap[selectedMetric],
        name: selectedMetric,
        nonInteractive: true,
        flex: 1,
        renderer: item => metricFormatter(selectedMetric)(item.metrics?.[selectedMetric] ?? 0)
      },
      {
        label: '% ∆',
        name: 'percentChange',
        nonInteractive: true,
        width: 35,
        renderer: item => {
          const metric = item.metrics?.[selectedMetric] ?? 0;
          const otherMetric = item.metrics_other?.[selectedMetric] ?? 0;
          const pct = Math.round(((metric - otherMetric) / otherMetric) * 100);
          // Invert the color for CPX/CPC/CPM since a decrease in those metrics is good.
          const color =
            (pct < 0 && !metricInverted) || (pct > 0 && metricInverted) ? 'red' : 'green';
          return pct ? (
            <div style={{ color }}>{Number.isFinite(pct) ? `${pct}%` : '--'}</div>
          ) : (
            '--'
          );
        }
      },
      {
        label: metricsToPrettyNameMap[selectedMetric],
        name: `${selectedMetric}_other`,
        nonInteractive: true,
        flex: 1,
        renderer: item => metricFormatter(selectedMetric)(item.metrics_other?.[selectedMetric] ?? 0)
      },
      {
        label: 'Date',
        name: 'date_other',
        width: 60,
        nonInteractive: true,
        renderer: item => formatDateLabel(item.date_other, {}, '', true) || '-'
      }
    ];
    const superHeaderList: SuperTopItemType<string>[] = [
      { span: 2, data: 'Primary Period' },
      { span: 1, data: '' },
      { span: 2, data: 'Comparison Period' }
    ];
    return [headerList, superHeaderList] as const;
  }, [metricInverted, metricsToPrettyNameMap, selectedMetric, metricFormatter]);

  const totals = useMemo(
    () => ({
      date: '',
      date_other: '',
      percentChange: '',
      [selectedMetric]:
        valueToggle === MetricTotalType.TOTAL || valueToggle === MetricTotalType.OVERALL
          ? metricFormatter(selectedMetric)(total)
          : metricFormatter(selectedMetric)(average),
      [`${selectedMetric}_other`]:
        valueToggle === MetricTotalType.TOTAL || valueToggle === MetricTotalType.OVERALL
          ? metricFormatter(selectedMetric)(otherTotal)
          : metricFormatter(selectedMetric)(otherAverage)
    }),
    [average, otherAverage, otherTotal, selectedMetric, total, valueToggle, metricFormatter]
  );

  const totalsRenderer: CellRenderer<Element | number | string | undefined> = ({
    data: cellData,
    style = {},
    classes = []
  }) => (
    <div style={style} className={cn(classes, 'grand-total-cell')}>
      {typeof cellData === 'string' || typeof cellData === 'number' ? cellData : ''}
    </div>
  );

  const prettySelectedMetric =
    metricOptions.find(item => item.value === selectedMetric)?.label || selectedMetric;
  const subHeaderValue =
    !isOverallMetric && valueToggle === MetricTotalType.TOTAL ? 'Total' : 'Overall';
  const primaryDateRange = data.length ? `${data[0].date}–${data[data.length - 1].date}` : '';
  const comparisonDateRange = otherData.length
    ? `vs_${otherData[0].date}–${otherData[otherData.length - 1].date}`
    : '';
  const fileNameIdentifiers = [
    ...pngFileIdentifiers,
    prettySelectedMetric,
    subHeaderValue,
    primaryDateRange,
    comparisonDateRange
  ];

  const excelDownload = () => {
    const resolvedFileName = fileNameIdentifiers.join('_');
    const exportData = [...data, ...otherData].map(({ date, metrics }) => ({
      date,
      ...Object.entries(metrics).reduce<Record<string, number>>((acc, [key, value]) => {
        acc[metricsToPrettyNameMap[key]] = value;
        return acc;
      }, {})
    }));
    const sheets: Sheet[] = [{ name: 'Spark Chart', data: exportData }];
    if (dataInfo) {
      sheets.push({ name: 'Data Info', data: dataInfo });
    }
    exportMultiSheetToExcel(sheets, resolvedFileName);
  };

  const pngDownload = async () => {
    await downloadPNG(
      `.spark-chart-container${defaultMetric ? `.${defaultMetric}` : ''}`,
      fileNameIdentifiers,
      pngLabel,
      ['.spark-chart-actions']
    );
  };

  const sortedMetricOptions = useMemo(
    () => [...metricOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [metricOptions]
  );

  const percentChangeNumber = typeof percentChange === 'number' ? percentChange : NaN;

  const tooltipContent = useMemo(
    () => (
      <SparkTooltipContent
        selectedMetric={selectedMetric}
        prettyName={metricsToPrettyNameMap[selectedMetric]}
        formatValue={metricFormatter(selectedMetric)}
      />
    ),
    [selectedMetric, metricsToPrettyNameMap, metricFormatter]
  );

  return (
    <div
      className={cn('spark-chart-container', defaultMetric)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className='spark-chart-header'>
        <div className='spark-chart-title'>
          <Dropdown<K>
            size={Size.LG}
            overlayPlacement={Placement.RIGHT}
            type={DropdownToggleType.WIDGET_TITLE}
            value={selectedMetric}
            options={sortedMetricOptions}
            onChange={option => {
              setSelectedMetric(option);
              setValueToggle(
                columnMetaDataMap[option]?.aggregator
                  ? MetricTotalType.OVERALL
                  : MetricTotalType.TOTAL
              );
            }}
          />
        </div>
        <div className='spark-chart-actions'>
          <GraphTableToggleButton
            graphView={graphView}
            setGraphView={setGraphView}
            widgetName={`spark_chart_${selectedMetric}`}
            size={Size.MD}
          />
          <DownloadDropdown
            onClickOptions={[excelDownload, pngDownload]}
            disabledMenuOptions={{ PNG: !graphView }}
          />
        </div>
      </div>
      <div className='spark-chart-sub-header'>
        {graphView && (
          <div className='value'>
            {metricFormatter(selectedMetric)(
              valueToggle === MetricTotalType.AVERAGE ? average : total
            )}
          </div>
        )}
        <div className='spark-chart-toggle-selector'>
          {[MetricTotalType.OVERALL, MetricTotalType.TOTAL, MetricTotalType.AVERAGE].map(
            (type, i) => {
              const isCorrectType = isOverallMetric ? i === 0 : i > 0;
              if (!isCorrectType) return undefined;

              const selected = valueToggle === type;
              if (selected) {
                return (
                  <div key={type} className='spark-chart-toggle-item selected'>
                    {METRIC_TOTAL_TYPE_PRETTY_NAMES[type]}
                  </div>
                );
              }

              return (
                <InteractiveDiv
                  key={type}
                  className='spark-chart-toggle-item deselected'
                  onClick={() => setValueToggle(type)}
                >
                  {METRIC_TOTAL_TYPE_PRETTY_NAMES[type]}
                </InteractiveDiv>
              );
            }
          )}
        </div>
      </div>
      <div className={cn('spark-chart-body', graphView ? 'graph-view' : 'table-view')}>
        {graphView ? (
          <AutoSizer
            renderProp={({ width = 0, height = 0 }) => (
              <ComposedChart
                width={width}
                height={height}
                data={joinedData}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                onMouseMove={(_state, e) => {
                  // Flip the tooltip to the cursor's left side when the cursor sits
                  // close to the right edge of the viewport, so the escaped tooltip
                  // never spills off the page.
                  if (!e || typeof e.clientX !== 'number') return;
                  setTooltipReverseX(window.innerWidth - e.clientX < TOOLTIP_REVERSE_THRESHOLD_PX);
                }}
              >
                <Tooltip
                  content={tooltipContent}
                  allowEscapeViewBox={{ x: true }}
                  reverseDirection={{ x: tooltipReverseX, y: false }}
                />
                <XAxis hide dataKey={data.length > otherData.length ? 'date' : 'date_other'} />
                <YAxis hide />
                <defs>
                  <linearGradient id='gradient' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='1%' stopColor={Brand30} />
                    <stop offset='95%' stopColor={Brand0} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type='linear'
                  name={selectedMetric}
                  dataKey={`metrics.${selectedMetric}`}
                  stackId='1'
                  activeDot={false}
                  fillOpacity={1}
                  strokeWidth={3}
                  stroke={Brand50}
                  isAnimationActive={false}
                  fill='url(#gradient)'
                />
                {/* Only mounted on hover so the resting y-domain fits the primary series alone. */}
                {hover && (
                  <Line
                    name={`${selectedMetric}_other`}
                    dataKey={`metrics_other.${selectedMetric}`}
                    type='linear'
                    strokeWidth={LINE_STROKE_WIDTH}
                    activeDot={false}
                    dot={false}
                    isAnimationActive={false}
                    strokeDasharray='4'
                  />
                )}
              </ComposedChart>
            )}
          />
        ) : (
          <BPMTable
            data={joinedData}
            headers={headers}
            superHeaders={superHeaders}
            filterBar={false}
            totals={totals}
            totalsRenderer={totalsRenderer}
            headerHeight={30}
            superHeaderHeight={30}
            bottomRowHeight={30}
          />
        )}
      </div>
      {graphView && (
        <div className='spark-chart-footer'>
          <div className='comparison'>
            <div className='range-date'>
              {makePrettyDateRange(
                (otherData[0] || {}).date,
                (otherData[otherData.length - 1] || {}).date
              )}
            </div>
            <div className='range-label-row'>
              <div className='range-label'>
                {`${valueToggle === MetricTotalType.AVERAGE ? 'Avg. ' : ''}${
                  metricsToPrettyNameMap[selectedMetric]
                }:`}
              </div>
              <div className='value'>
                {metricFormatter(selectedMetric)(
                  valueToggle === MetricTotalType.OVERALL || valueToggle === MetricTotalType.TOTAL
                    ? otherTotal
                    : otherAverage
                )}
              </div>
            </div>
          </div>
          <PercentChange
            percentChange={percentChangeNumber}
            invertTone={metricInverted}
            size={Size.MD}
            stretchMode={StretchMode.FIT}
          />
        </div>
      )}
    </div>
  );
};
